import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import ChapterSidebar from '../components/ChapterSidebar';
import NotesSidebar from '../components/NotesSidebar';
import MainContent from '../components/MainContent';
import { Book, Chapter } from '../types';

interface BookEditorProps {
  user: User;
  onSignOut: () => void;
}

export default function BookEditor({ user }: BookEditorProps) {
  const [books, setBooks] = useState<Book[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  const [chapterTitle, setChapterTitle] = useState('');
  const [savingStatus, setSavingStatus] = useState<'saved' | 'saving' | 'error'>('saved');

  // Load books and chapters from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load books
        const { data: booksData, error: booksError } = await supabase
          .from('books')
          .select('*')
          .order('last_modified', { ascending: false });

        if (booksError) throw booksError;

        const formattedBooks: Book[] = booksData.map(row => ({
          id: row.id,
          title: row.title,
          description: row.description,
          lastModified: new Date(row.last_modified).getTime(),
          user_id: row.user_id
        }));

        setBooks(formattedBooks);

        // Load chapters
        const { data: chaptersData, error: chaptersError } = await supabase
          .from('chapters')
          .select('*')
          .order('position', { ascending: true });

        if (chaptersError) throw chaptersError;

        const formattedChapters: Chapter[] = chaptersData.map(row => ({
          id: row.id,
          title: row.title,
          content: row.content,
          lastModified: new Date(row.last_modified).getTime(),
          book_id: row.book_id,
          user_id: row.user_id,
          position: row.position
        }));

        setChapters(formattedChapters);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  const createNewBook = async () => {
    try {
      setSavingStatus('saving');
      const newBook = {
        title: 'New Book',
        description: '',
        user_id: user.id
      };

      const { data, error } = await supabase
        .from('books')
        .insert(newBook)
        .select()
        .single();

      if (error) throw error;

      const formattedBook: Book = {
        id: data.id,
        title: data.title,
        description: data.description,
        lastModified: new Date(data.last_modified).getTime(),
        user_id: data.user_id
      };

      setBooks(prev => [formattedBook, ...prev]);
      setSelectedBookId(formattedBook.id);
      setSavingStatus('saved');
    } catch (error) {
      console.error('Error creating book:', error);
      setSavingStatus('error');
    }
  };

  const createNewChapter = async (bookId: string) => {
    try {
      setSavingStatus('saving');
      const bookChapters = chapters.filter(c => c.book_id === bookId);
      const maxPosition = Math.max(...bookChapters.map(c => c.position), -1);
      
      const newChapter = {
        title: 'New Chapter',
        content: '<p>Start writing your new chapter...</p>',
        book_id: bookId,
        user_id: user.id,
        position: maxPosition + 1
      };

      const { data, error } = await supabase
        .from('chapters')
        .insert(newChapter)
        .select()
        .single();

      if (error) throw error;

      const formattedChapter: Chapter = {
        id: data.id,
        title: data.title,
        content: data.content,
        lastModified: new Date(data.last_modified).getTime(),
        book_id: data.book_id,
        user_id: data.user_id,
        position: data.position
      };

      setChapters(prev => [...prev, formattedChapter]);
      setSelectedChapterId(formattedChapter.id);
      setChapterTitle(formattedChapter.title);
      setSavingStatus('saved');
    } catch (error) {
      console.error('Error creating chapter:', error);
      setSavingStatus('error');
    }
  };

  const updateBookTitle = async (bookId: string, newTitle: string) => {
    try {
      setSavingStatus('saving');
      const { error } = await supabase
        .from('books')
        .update({
          title: newTitle,
          last_modified: new Date().toISOString()
        })
        .eq('id', bookId);

      if (error) throw error;

      setBooks(prev => prev.map(book =>
        book.id === bookId
          ? { ...book, title: newTitle, lastModified: Date.now() }
          : book
      ));
      setSavingStatus('saved');
    } catch (error) {
      console.error('Error updating book title:', error);
      setSavingStatus('error');
    }
  };

  const updateChapter = async (content: string) => {
    if (!selectedChapterId) return;
    
    try {
      setSavingStatus('saving');
      const { error } = await supabase
        .from('chapters')
        .update({
          content,
          last_modified: new Date().toISOString()
        })
        .eq('id', selectedChapterId);

      if (error) throw error;

      setChapters(prev => prev.map(chapter =>
        chapter.id === selectedChapterId
          ? { ...chapter, content, lastModified: Date.now() }
          : chapter
      ));
      setSavingStatus('saved');
    } catch (error) {
      console.error('Error updating chapter:', error);
      setSavingStatus('error');
    }
  };

  const updateChapterTitle = async (newTitle: string) => {
    if (!selectedChapterId) return;
    
    try {
      setSavingStatus('saving');
      const { error } = await supabase
        .from('chapters')
        .update({
          title: newTitle,
          last_modified: new Date().toISOString()
        })
        .eq('id', selectedChapterId);

      if (error) throw error;

      setChapters(prev => prev.map(chapter =>
        chapter.id === selectedChapterId
          ? { ...chapter, title: newTitle, lastModified: Date.now() }
          : chapter
      ));
      setChapterTitle(newTitle);
      setSavingStatus('saved');
    } catch (error) {
      console.error('Error updating chapter title:', error);
      setSavingStatus('error');
    }
  };

  const deleteChapter = async (chapterId: string) => {
    try {
      setSavingStatus('saving');
      const { error } = await supabase
        .from('chapters')
        .delete()
        .eq('id', chapterId);

      if (error) throw error;

      setChapters(prev => prev.filter(chapter => chapter.id !== chapterId));
      if (selectedChapterId === chapterId) {
        setSelectedChapterId(null);
        setChapterTitle('');
      }
      setSavingStatus('saved');
    } catch (error) {
      console.error('Error deleting chapter:', error);
      setSavingStatus('error');
    }
  };

  const handleReorderChapters = async (bookId: string, reorderedChapters: Chapter[]) => {
    try {
      setSavingStatus('saving');
      
      // Prepare the updates
      const updates = reorderedChapters.map((chapter) => ({
        id: chapter.id,
        position: chapter.position,
        title: chapter.title,
        book_id: chapter.book_id,
        user_id: chapter.user_id
      }));

      const { error } = await supabase
        .from('chapters')
        .upsert(updates, { onConflict: 'id' });

      if (error) throw error;

      // Update local state
      setChapters(prev => {
        const otherChapters = prev.filter(c => c.book_id !== bookId);
        return [...otherChapters, ...reorderedChapters].sort((a, b) => a.position - b.position);
      });

      setSavingStatus('saved');
    } catch (error) {
      console.error('Error reordering chapters:', error);
      setSavingStatus('error');
    }
  };

  const selectedChapter = chapters.find(chapter => chapter.id === selectedChapterId);

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      <ChapterSidebar
        books={books}
        chapters={chapters}
        selectedBookId={selectedBookId}
        selectedChapterId={selectedChapterId}
        onCreateBook={createNewBook}
        onCreateChapter={createNewChapter}
        onSelectBook={(id) => {
          setSelectedBookId(id);
          setSelectedChapterId(null);
        }}
        onSelectChapter={(id) => {
          setSelectedChapterId(id);
          const chapter = chapters.find(c => c.id === id);
          if (chapter) {
            setChapterTitle(chapter.title);
          }
        }}
        onUpdateBookTitle={updateBookTitle}
        onDeleteChapter={deleteChapter}
        onReorderChapters={handleReorderChapters}
      />

      <MainContent
        selectedChapter={selectedChapter}
        selectedBookId={selectedBookId}
        chapterTitle={chapterTitle}
        savingStatus={savingStatus}
        onChapterTitleChange={updateChapterTitle}
        onChapterContentUpdate={updateChapter}
        onCreateBook={createNewBook}
        onCreateChapter={createNewChapter}
      />

      <NotesSidebar
        selectedChapterId={selectedChapterId}
        userId={user.id}
        defaultView="notes"
      />
    </div>
  );
}